import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IHook, IHookPayload, IHookRequest, WildCardEvent } from '../models/hook';
import { mergeMap, take } from 'rxjs/operators';
import { SelectorWrapperService } from './selector-wrapper.service';
import { IHookshotConfig } from '@boldpenguin/sdk';

@Injectable({
  providedIn: 'root',
})
export class HooksService {
  private hookshotConfig$: Observable<IHookshotConfig>;

  constructor(
    private selectorWrapperService: SelectorWrapperService,
    private http: HttpClient,
  ) {
    this.hookshotConfig$ = this.selectorWrapperService.getHookshotConfig$();
  }

  getHooks(): Observable<IHook[]> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.get<IHook[]>(`${config.uri}/hooks`, this.getOptions())
      })
    )
  }

  getHook(id: string): Observable<IHook> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.get<IHook>(`${config.uri}/hooks/${id}`, this.getOptions())
      })
    );
  }

  addHook(hook: IHookPayload): Observable<IHook> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.post<IHook>(`${config.uri}/hooks`, { hook }, this.getOptions())
      })
    );
  }

  testHook(id: string): Observable<any> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.post<IHook>(`${config.uri}/hooks/${id}/test_deliveries`, {}, this.getOptions())
      })
    );
  }

  updateHook(hook: IHook): Observable<IHook> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.patch<IHook>(`${config.uri}/hooks/${hook.id}/`,{hook: this.getHookPayload(hook)}, this.getOptions())
      })
    );
  }

  deleteHook(hook: IHook): Observable<any> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.delete<IHook>(`${config.uri}/hooks/${hook.id}/`, this.getOptions())
      })
    );
  }

  saveHook(hook: IHook): Observable<IHook> {
    if (!hook.id) {
      return this.addHook(this.getHookPayload(hook));
    } else {
      return this.updateHook(hook);
    }
  }

  getHookRequests(hookId: string, onlyFailures?: boolean): Observable<IHookRequest[]> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        const options = this.getOptions(onlyFailures ? {filter_failed: true} : undefined)

        return this.http.get<IHookRequest[]>(`${config.uri}/hooks/${hookId}/requests`, options)
      })
    );
  }

  redeliverRequest(hookId: string, requestId: string): Observable<IHookRequest> {
    return this.hookshotConfig$.pipe(
      take(1),
      mergeMap(config => {
        return this.http.post<IHookRequest>(`${config.uri}/hooks/${hookId}/requests/${requestId}/redeliver`, undefined, this.getOptions())
      })
    )
  }



  private getOptions(params?: { [p: string]: string | number | boolean | readonly (string | number | boolean)[] }): {
    headers: HttpHeaders,
    withCredentials: boolean,
    params?: HttpParams
  } {
    const options = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      withCredentials: true
    } as { headers: HttpHeaders; withCredentials: boolean, params?: HttpParams };

    if(params) {
      options.params = new HttpParams({fromObject: params})
    }

    return options
  }

  private getHookPayload(hook: IHook) {
    return {
        active: hook.active,
        url: hook.config.url,
        secret: hook.config.secret,
        header: hook.config.header,
        events: hook.wildcard ? [WildCardEvent.ALL] : hook.events
      } as IHookPayload
  }
}
